import { LocalDate } from '@js-joda/core';
import { SpoolStatus, SpoolUpdateForm, UnitSystem } from 'providers/api';
import { distanceImperialToMetric, distanceMetricToImperial } from 'utils/unitConversions';
import PCPipe from '../MineModel/Pipes/PCPipe';
import PCSpool from '../MineModel/Spools/PCSpool';

export interface SpoolFormValues {
  name: string;
  pipeId: string;
  allowFlowAB: boolean;
  allowFlowBA: boolean;
  /** In the project's display unit, not in metres. */
  chokeLength: number;
  status: SpoolStatus;
  installedOn: LocalDate | null;
  removedOn: LocalDate | null;
  other: string;
  level: string;
  area: string;
}

export const SPOOL_NAME_MAX_LENGTH = 128;
export const SPOOL_OTHER_MAX_LENGTH = 2000;
export const SPOOL_TAG_MAX_LENGTH = 128;

const DISPLAY_DECIMALS = 3;

const roundForDisplay = (value: number) => {
  const factor = 10 ** DISPLAY_DECIMALS;
  return Math.round(value * factor) / factor;
};

export const lengthUnitLabel = (unitSystem: UnitSystem) => (unitSystem === UnitSystem.Imperial ? 'ft' : 'm');

export const metresToDisplay = (metres: number, unitSystem: UnitSystem) => roundForDisplay(
  unitSystem === UnitSystem.Imperial ? distanceMetricToImperial(metres) : metres,
);

export const displayToMetres = (value: number, unitSystem: UnitSystem) => (
  unitSystem === UnitSystem.Imperial ? distanceImperialToMetric(value) : value
);

export const spoolStraightLength = (spool: PCSpool) => spool.pointA.coords.length3dTo(spool.pointB.coords);

export const toSpoolFormValues = (spool: PCSpool, unitSystem: UnitSystem): SpoolFormValues => ({
  name: spool.name ?? '',
  pipeId: spool.pipe?.pipeId ?? '',
  allowFlowAB: spool.allowFlowAB,
  allowFlowBA: spool.allowFlowBA,
  chokeLength: metresToDisplay(spool.chokeLength || 0, unitSystem),
  status: spool.status,
  installedOn: spool.installedOn ?? null,
  removedOn: spool.removedOn ?? null,
  other: spool.other ?? '',
  level: spool.level ?? '',
  area: spool.area ?? '',
});

const sameDate = (a: LocalDate | null, b: LocalDate | null) => (a && b ? a.equals(b) : a === b);

// Only what the user changed goes on the wire. The endpoint leaves an omitted field alone, so an
// untouched flow-loop length is never re-sent through a feet round trip and an untouched pipe is
// never re-resolved.
export const toSpoolUpdateForm = (
  spoolId: string,
  initial: SpoolFormValues,
  values: SpoolFormValues,
  unitSystem: UnitSystem,
): SpoolUpdateForm | null => {
  const installedOnChanged = !sameDate(values.installedOn, initial.installedOn);
  const removedOnChanged = !sameDate(values.removedOn, initial.removedOn);
  const changes: Omit<SpoolUpdateForm, 'spoolId'> = {
    ...(values.pipeId && values.pipeId !== initial.pipeId ? { pipeId: values.pipeId } : {}),
    ...(values.allowFlowAB !== initial.allowFlowAB ? { allowFlowAB: values.allowFlowAB } : {}),
    ...(values.allowFlowBA !== initial.allowFlowBA ? { allowFlowBA: values.allowFlowBA } : {}),
    ...(values.chokeLength !== initial.chokeLength ? { chokeLength: displayToMetres(values.chokeLength, unitSystem) } : {}),
    // An empty name, Other, level or area tells the server to clear it; a date is cleared with its flag.
    ...(values.name.trim() !== initial.name.trim() ? { name: values.name.trim() } : {}),
    ...(values.other.trim() !== initial.other.trim() ? { other: values.other.trim() } : {}),
    ...(values.level.trim() !== initial.level.trim() ? { level: values.level.trim() } : {}),
    ...(values.area.trim() !== initial.area.trim() ? { area: values.area.trim() } : {}),
    ...(installedOnChanged && values.installedOn ? { installedOn: values.installedOn } : {}),
    ...(installedOnChanged && !values.installedOn ? { clearInstalledOn: true } : {}),
    ...(removedOnChanged && values.removedOn ? { removedOn: values.removedOn } : {}),
    ...(removedOnChanged && !values.removedOn ? { clearRemovedOn: true } : {}),
    ...(values.status !== initial.status ? { status: values.status } : {}),
  };

  return Object.keys(changes).length === 0 ? null : { spoolId, ...changes };
};

// What the saved patch means for the spool already in the scene, so it can be updated in place
// rather than reloading the whole mine model.
export const toSpoolPatch = (update: SpoolUpdateForm, pipes: Record<string, PCPipe>): Partial<PCSpool> => {
  const pipe = update.pipeId ? pipes[update.pipeId] : undefined;
  return {
    ...(pipe ? { pipe } : {}),
    ...(update.allowFlowAB !== undefined ? { allowFlowAB: update.allowFlowAB } : {}),
    ...(update.allowFlowBA !== undefined ? { allowFlowBA: update.allowFlowBA } : {}),
    ...(update.chokeLength !== undefined ? { chokeLength: update.chokeLength } : {}),
    ...(update.name !== undefined ? { name: update.name || undefined } : {}),
    ...(update.installedOn ? { installedOn: update.installedOn } : {}),
    ...(update.clearInstalledOn ? { installedOn: undefined } : {}),
    ...(update.removedOn ? { removedOn: update.removedOn } : {}),
    ...(update.clearRemovedOn ? { removedOn: undefined } : {}),
    ...(update.status !== undefined ? { status: update.status } : {}),
    ...(update.other !== undefined ? { other: update.other || undefined } : {}),
    ...(update.level !== undefined ? { level: update.level || undefined } : {}),
    ...(update.area !== undefined ? { area: update.area || undefined } : {}),
  };
};
